import {
  MilitaryBase,
  CompletionSuggestBaseResult,
  GeoPoint,
  PhraseSuggestBaseResult,
  Highlight,
  GeoProximity,
  GeoBoundingBox,
  SearchBaseResult,
} from '@zoocasa/go-search';
import {
  getGoSearchHost,
  isServerSide,
} from 'utils/host-config';
import {
  getBase,
  getBaseNameSuggestions,
  getBasesByBounds,
  getBasesByProximity,
  getBaseSuggestions,
} from './fetcher';

export type MilitaryApiType = {
  getBaseBySlug(slug: string, signal?: AbortSignal): Promise<MilitaryBase | undefined>;
  getBaseById(id: string, signal?: AbortSignal): Promise<MilitaryBase | undefined>;
  getBaseByName(name: string, signal?: AbortSignal): Promise<MilitaryBase | undefined>;

  suggestBase(query: string, size: number, proximity?: GeoPoint, signal?: AbortSignal): Promise<CompletionSuggestBaseResult[]>;

  suggestBaseName(query: string, size?: number, highlight?: Highlight, confidence?: number, signal?: AbortSignal): Promise<PhraseSuggestBaseResult | undefined>;

  searchBasesByProximity(proximity: GeoProximity, size?: number, signal?: AbortSignal): Promise<SearchBaseResult[]>

  searchBasesWithinBounds(bounds: GeoBoundingBox, size?: number, signal?: AbortSignal): Promise<SearchBaseResult[]>
}

class MilitaryApi implements MilitaryApiType {

  private constructor(private host: string) {}

  static create(host: string = getGoSearchHost(isServerSide())): MilitaryApi {
    return new MilitaryApi(host);
  }

  async getBaseBySlug(slug: string, signal?: AbortSignal): Promise<MilitaryBase | undefined> {
    return getBase({ slug }, signal, this.host);
  }

  async getBaseById(id: string, signal?: AbortSignal): Promise<MilitaryBase | undefined> {
    return getBase({ id }, signal, this.host);
  }

  async getBaseByName(name: string, signal?: AbortSignal): Promise<MilitaryBase | undefined> {
    return getBase({ name }, signal, this.host);
  }

  async suggestBase(query: string, size: number = 5, proximity?: GeoPoint, signal?: AbortSignal): Promise<CompletionSuggestBaseResult[]> {
    if (query?.length === 0) {
      return [];
    }
    return getBaseSuggestions(query, size, proximity, signal, this.host);
  }

  async suggestBaseName(query: string, size?: number, highlight?: Highlight, confidence?: number, signal?: AbortSignal): Promise<PhraseSuggestBaseResult | undefined> {
    if (query?.length === 0) {
      return undefined;
    }
    return getBaseNameSuggestions(query, size, highlight, confidence, signal, this.host);
  }

  async searchBasesByProximity(proximity: GeoProximity, size?: number, signal?: AbortSignal): Promise<SearchBaseResult[]> {
    return getBasesByProximity(proximity, size, signal, this.host);
  }

  async searchBasesWithinBounds(bounds: GeoBoundingBox, size?: number, signal?: AbortSignal): Promise<SearchBaseResult[]> {
    return getBasesByBounds(bounds, size, signal, this.host);
  }
}

/**
 * Creates a new MilitaryApi instance with the given host.
 * @param host The host to use for the MilitaryApi instance.
 * @returns A new MilitaryApi instance.
 */
export const createMilitaryApi = MilitaryApi.create;